import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { alpha, makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ViewListIcon from '@material-ui/icons/ViewList';
import CircleIcon from '@material-ui/icons/FiberManualRecord';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import MuiSwitch from '@material-ui/core/Switch';
import { useTranslation } from 'react-i18next';

import { IconButton, Typography } from '@material-ui/core';
import { AccountCircle, Close } from '@material-ui/icons';

import LanguageSelect from '../LanguageSelect';
import { GetHelp, InfoButton } from '../FormWidgets/Description';

import { statusColors } from '../Constants/constants';
import { useAuth } from '../Hooks/authHook';
import { useProxySelector } from '../Hooks/useProxySelector';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';
import { isZoned } from '../Utils/utils';
import {
  getSettingsRestorePreference,
  setSettingsRestorePreference
} from '../Utils/settings_draft';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 320,
    maxWidth: 'calc(100vw - 24px)',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    '& .MuiButton-root': { margin: 0, minHeight: 44, color: 'inherit' }
  },
  accountButton: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    paddingLeft: 0,
    '& .MuiButton-startIcon': { marginLeft: 0 }
  },
  closeButton: {
    width: 44,
    height: 44,
    flexShrink: 0
  },
  settings: {
    padding: theme.spacing(2, 2.5, 2.5)
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 52,
    '& .MuiSwitch-root': { flexShrink: 0, marginRight: -8 }
  },
  label: {
    minWidth: 0,
    fontSize: 14,
    lineHeight: 1.5,
    overflowWrap: 'anywhere'
  },
  labelWithHelp: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    '& > span:last-child': { flexShrink: 0 },
    // Keep the 44px touch target, but place the icon closer to its label.
    '& .MuiIconButton-root': { marginLeft: 0 }
  },
  settingGroup: {
    marginTop: theme.spacing(2)
  },
  sliderLockButtons: {
    width: '100%',
    padding: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover
  },
  sliderLockButton: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    border: '0 !important',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    fontSize: 14,
    '&[aria-pressed="true"]': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main)
    },
    '& .MuiButton-label': {
      overflowWrap: 'normal',
      whiteSpace: 'nowrap'
    }
  },
  restoreSelect: {
    fontSize: 14,
    borderRadius: theme.shape.borderRadius,
    '& .MuiSelect-root': { paddingTop: 12, paddingBottom: 12 }
  },
  restoreOption: {
    fontSize: 14,
    minHeight: 44,
    whiteSpace: 'normal'
  },
  controllers: {
    padding: theme.spacing(2, 1, 1)
  },
  navigationItem: {
    minHeight: 44,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.14)
    },
    '&.Mui-selected .MuiListItemText-primary': { fontWeight: 600 }
  },
  navigationIcon: {
    minWidth: 32,
    color: theme.palette.text.secondary
  },
  statusDot: {
    fontSize: 16,
    marginLeft: 2
  },
  controllerList: {
    marginTop: theme.spacing(1)
  }
}));

// Disabled until GUI zoom no longer breaks pointer alignment on sliders/touch controls.
const SHOW_MENU_ZOOM_CONTROLS = false;

export default function MainMenu({
  open,
  onClose,
  toggleTheme,
  currTheme,
  toggleUserMenu,
  onNavigate,
  setLang,
  lockSliderSetting,
  setLockSliderSetting
}) {
  const classes = useStyles();
  const location = useLocation();
  const auth = useAuth();
  let { t } = useTranslation();
  const editModeHelp = GetHelp('edit_mode');
  const sliderLockHelp = GetHelp('slider_lock');
  const restoreHelp = GetHelp('auto_save_preference');
  const [restorePreference, setRestorePreference] = React.useState(
    () => getSettingsRestorePreference() ?? 'ask'
  );
  // The restore dialog can remember a choice while this drawer is closed.
  React.useEffect(() => {
    if (open) setRestorePreference(getSettingsRestorePreference() ?? 'ask');
  }, [open]);

  const stateManager = getStateManager();
  const controllers = useSnapshot(stateManager.controllers);
  // A snapshot of the store root would be checked on every live update.
  const editMode = useProxySelector(stateManager, (store) => store.editMode);

  const zoom = function (amount) {
    let new_zoom = Number(localStorage.getItem('zoom') ?? 1) + amount;
    document.body.style.zoom = new_zoom;
    localStorage.setItem('zoom', new_zoom);
  };

  return (
    <Drawer anchor="left" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <Button
          className={classes.accountButton}
          color="inherit"
          onClick={toggleUserMenu(true)}
          startIcon={<AccountCircle />}
        >
          {t('my_account')}
        </Button>
        <LanguageSelect setLang={setLang} onSelect={onNavigate} />

        {SHOW_MENU_ZOOM_CONTROLS && (
          <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
            <IconButton
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                zoom(0.2);
              }}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                zoom(-0.2);
              }}
            >
              <ZoomOutIcon />
            </IconButton>
          </div>
        )}
        <IconButton className={classes.closeButton} onClick={onClose} aria-label={t('close')}>
          <Close fontSize="small" />
        </IconButton>
      </div>
      <Divider />
      <div className={classes.settings}>
        <div className={classes.switchRow}>
          <Typography component="label" htmlFor="menu-dark-mode" className={classes.label}>
            {t('dark_mode')}
          </Typography>
          <MuiSwitch
            id="menu-dark-mode"
            checked={currTheme !== 'light'}
            onChange={toggleTheme}
            color="primary"
          />
        </div>
        <div className={classes.switchRow}>
          <div className={classes.labelWithHelp}>
            <Typography component="label" htmlFor="menu-edit-mode" className={classes.label}>
              {t('edit_mode')}
            </Typography>
            <InfoButton
              brief={editModeHelp.brief}
              content={editModeHelp.content}
              img={editModeHelp.img}
              table={editModeHelp.table}
              ariaLabel={`${t('info')}: ${t('edit_mode')}`}
            />
          </div>
          <MuiSwitch
            id="menu-edit-mode"
            checked={editMode}
            onChange={() => (stateManager.editMode = !stateManager.editMode)}
            color="primary"
          />
        </div>
        <div className={classes.settingGroup}>
          <div className={classes.labelWithHelp}>
            <Typography className={classes.label}>{t('slider_lock')}</Typography>
            <InfoButton
              brief={t('slider_lock')}
              content={sliderLockHelp.content}
              ariaLabel={`${t('info')}: ${t('slider_lock')}`}
            />
          </div>
          <ButtonGroup
            className={classes.sliderLockButtons}
            color="primary"
            aria-label={t('slider_lock')}
          >
            {['auto', 'on', 'off'].map((mode) => (
              <Button
                className={classes.sliderLockButton}
                key={mode}
                onClick={() => setLockSliderSetting(mode)}
                variant="text"
                aria-pressed={lockSliderSetting === mode}
              >
                {t(`slider_lock_${mode}`)}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <div className={classes.settingGroup}>
          <div className={classes.labelWithHelp}>
            <Typography className={classes.label} id="menu-restore-label">
              {t('auto_save_preference')}
            </Typography>
            <InfoButton
              brief={t('auto_save_preference')}
              content={restoreHelp.content}
              ariaLabel={`${t('info')}: ${t('auto_save_preference')}`}
            />
          </div>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              className={classes.restoreSelect}
              labelId="menu-restore-label"
              value={restorePreference}
              onChange={(event) => {
                setSettingsRestorePreference(event.target.value);
                setRestorePreference(event.target.value);
              }}
            >
              <MenuItem className={classes.restoreOption} value="ask">
                {t('auto_save_preference_ask')}
              </MenuItem>
              <MenuItem className={classes.restoreOption} value="1">
                {t('auto_save_preference_restore')}
              </MenuItem>
              <MenuItem className={classes.restoreOption} value="0">
                {t('auto_save_preference_discard')}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      {auth.user && (
        <React.Fragment>
          <Divider />
          <nav className={classes.controllers} aria-label={t('manage_controllers')}>
            <List disablePadding>
              <ListItem
                button
                className={classes.navigationItem}
                component={Link}
                role="link"
                to="/"
                onClick={onNavigate}
                selected={location.pathname === '/'}
                aria-current={location.pathname === '/' ? 'page' : undefined}
              >
                <ListItemIcon className={classes.navigationIcon}>
                  <ViewListIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('manage_controllers')}
                  primaryTypographyProps={{ className: classes.label }}
                />
              </ListItem>
            </List>
            <List disablePadding className={classes.controllerList}>
              {controllers.map((controller) => (
                <ListItem
                  button
                  key={controller.id}
                  className={classes.navigationItem}
                  component={Link}
                  role="link"
                  onClick={onNavigate}
                  selected={location.pathname.startsWith(`/controller/${controller.id}/`)}
                  aria-current={
                    location.pathname.startsWith(`/controller/${controller.id}/`)
                      ? 'page'
                      : undefined
                  }
                  to={
                    `/controller/${controller.id}` +
                    (isZoned(controller) ? '/zones/zone_0/info' : '/info')
                  }
                >
                  <ListItemIcon className={classes.navigationIcon}>
                    <CircleIcon
                      className={classes.statusDot}
                      style={{ color: statusColors[controller?.connection?.status ?? 'bad'] }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={controller.name}
                    primaryTypographyProps={{ className: classes.label }}
                  />
                </ListItem>
              ))}
            </List>
          </nav>
        </React.Fragment>
      )}
    </Drawer>
  );
}
